import SaveIcon from '@mui/icons-material/Save';
import React, { useState } from 'react';
import {
  ProductRecognitionBBox,
  ProductRecognitionLayerType,
  ProductRecognitionPolygon,
  ProductRecognitionResponse,
} from '../../../types';
import { LayerGroup } from './LayerGroup';

interface ProductRecognitionControlCenterProps {
  onRunModel: (options?: { triggerPogCompliance?: boolean }) => void;
  onSaveInference?: () => Promise<void>;
  productRecognitionData?: ProductRecognitionResponse | null;
  isLoading?: boolean;
  isSavingInference?: boolean;
  currentImageId?: number;
  currentImageElement?: HTMLImageElement;
  onEnabledLayersChange?: (enabledLayers: Set<string>) => void;
  enabledLayers: Set<string>;
  chainId?: number;
  storeId?: number;
  isDataFromCache?: boolean;
  onClearCache?: () => void;
}

interface LayerInfo {
  key: ProductRecognitionLayerType;
  label: string;
  color: string;
  bboxColor: string;
  enabledByDefault: boolean;
}

export const LAYER_CONFIG: LayerInfo[] = [
  {
    key: 'productPolygons',
    label: 'Product Polygons',
    color: 'bg-blue-500',
    bboxColor: '#3b82f6',
    enabledByDefault: true,
  },
  {
    key: 'cartonPolygons',
    label: 'Carton Polygons',
    color: 'bg-green-500',
    bboxColor: '#22c55e',
    enabledByDefault: true,
  },
  {
    key: 'priceTagBoxes',
    label: 'Price Tag Boxes',
    color: 'bg-yellow-500',
    bboxColor: '#eab308',
    enabledByDefault: true,
  },
  {
    key: 'modPolygons',
    label: 'Mod Polygons',
    color: 'bg-purple-500',
    bboxColor: '#a855f7',
    enabledByDefault: true,
  },
  {
    key: 'shelfBoxes',
    label: 'Shelf Boxes',
    color: 'bg-orange-500',
    bboxColor: '#f97316',
    enabledByDefault: true,
  },
  {
    key: 'templateBoxes',
    label: 'Template Boxes',
    color: 'bg-red-500',
    bboxColor: '#ef4444',
    enabledByDefault: true,
  },
  {
    key: 'templateBoxesUp',
    label: 'Template Boxes Up',
    color: 'bg-red-400',
    bboxColor: '#f87171',
    enabledByDefault: true,
  },
  {
    key: 'visualObstructions',
    label: 'Visual Obstruction',
    color: 'bg-pink-500',
    bboxColor: '#ec4899',
    enabledByDefault: false,
  },
  {
    key: 'merchandise',
    label: 'Merchandise',
    color: 'bg-cyan-500',
    bboxColor: '#06b6d4',
    enabledByDefault: false,
  },
  {
    key: 'hookArea',
    label: 'Hook Area',
    color: 'bg-indigo-500',
    bboxColor: '#6366f1',
    enabledByDefault: false,
  },
  {
    key: 'fridgeDoors',
    label: 'Fridge Doors',
    color: 'bg-teal-500',
    bboxColor: '#14b8a6',
    enabledByDefault: false,
  },
  {
    key: 'topStock',
    label: 'Top Stock',
    color: 'bg-amber-500',
    bboxColor: '#f59e0b',
    enabledByDefault: false,
  },
  {
    key: 'shelfEdges',
    label: 'Shelf Edges',
    color: 'bg-lime-500',
    bboxColor: '#84cc16',
    enabledByDefault: false,
  },
];

export const ProductRecognitionControlCenter: React.FC<ProductRecognitionControlCenterProps> = ({
  onRunModel,
  onSaveInference,
  productRecognitionData,
  isLoading = false,
  isSavingInference = false,
  currentImageId,
  currentImageElement,
  onEnabledLayersChange,
  enabledLayers,
  chainId,
  storeId,
  isDataFromCache = false,
  onClearCache,
}) => {
  const [searchText, setSearchText] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<ProductRecognitionLayerType>>(
    new Set()
  );
  const [triggerPogCompliance, setTriggerPogCompliance] = useState(false);

  // Check if product recognition data is from a different image
  const isDataFromDifferentImage =
    productRecognitionData &&
    currentImageId !== undefined &&
    productRecognitionData.imageId !== currentImageId;

  const toggleLayer = (layerKey: ProductRecognitionLayerType) => {
    const newSet = new Set(enabledLayers);
    if (newSet.has(layerKey)) {
      newSet.delete(layerKey);
    } else {
      newSet.add(layerKey);
    }
    // Notify parent component of layer changes
    onEnabledLayersChange?.(newSet);
  };

  const toggleGroupCollapse = (layerKey: ProductRecognitionLayerType) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(layerKey)) {
        newSet.delete(layerKey);
      } else {
        newSet.add(layerKey);
      }
      return newSet;
    });
  };

  const getLayerCount = (layerKey: ProductRecognitionLayerType): number => {
    if (!productRecognitionData) return 0;
    const data = productRecognitionData[layerKey];
    return Array.isArray(data) ? data.length : 0;
  };

  const renderLayerElements = (layerKey: ProductRecognitionLayerType) => {
    if (!productRecognitionData || !enabledLayers.has(layerKey)) return null;

    const data = productRecognitionData[layerKey];
    if (!Array.isArray(data) || data.length === 0) return null;

    const layerInfo = LAYER_CONFIG.find((l) => l.key === layerKey);
    if (!layerInfo) return null;

    // Filter data by search text (OCR text, product names, UPC, item numbers)
    // Shapes are now pre-calculated at API level
    const filteredData = (data as (ProductRecognitionBBox | ProductRecognitionPolygon)[]).filter(
      (item) => {
        if (!searchText.trim()) return true;
        const searchLower = searchText.toLowerCase();

        // Search in OCR text
        const ocrText = item.ocr?.text?.toLowerCase() || '';
        if (ocrText.includes(searchLower)) return true;

        // Search in viz candidates
        if (item.vizSimCandidates) {
          return item.vizSimCandidates.some((candidate) => {
            // Search product name
            const productName = candidate.productName?.toLowerCase() || '';
            if (productName.includes(searchLower)) return true;

            // Search normalized UPC
            const upc = candidate.normalizedUpc?.toLowerCase() || '';
            if (upc.includes(searchLower)) return true;

            // Search item number
            const itemNumber = candidate.itemNumber?.toLowerCase() || '';
            if (itemNumber.includes(searchLower)) return true;

            return false;
          });
        }

        return false;
      }
    );

    if (filteredData.length === 0) return null;

    return (
      <LayerGroup
        key={layerKey}
        layerKey={layerKey}
        layerLabel={layerInfo.label}
        layerColor={layerInfo.color}
        filteredData={filteredData}
        totalCount={data.length}
        searchText={searchText}
        isCollapsed={collapsedGroups.has(layerKey)}
        onToggleCollapse={() => toggleGroupCollapse(layerKey)}
        cameraImageElement={currentImageElement}
        chainId={chainId}
        storeId={storeId}
        cartonBoxes={productRecognitionData?.cartonBoxes}
      />
    );
  };

  return (
    <div className="space-y-6">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={triggerPogCompliance}
          onChange={(e) => setTriggerPogCompliance(e.target.checked)}
          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
        />
        <span className="text-sm font-medium text-gray-700">Trigger POG Compliance</span>
      </label>

      {/* Run Model Button and Cache Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              onRunModel({
                triggerPogCompliance,
              })
            }
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-md font-medium flex items-center gap-2"
          >
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            {isLoading ? 'Running Model...' : 'Run Model'}
          </button>

          {productRecognitionData && onSaveInference && (
            <button
              onClick={onSaveInference}
              disabled={isSavingInference}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-6 py-2 rounded-md font-medium flex items-center gap-2"
            >
              {isSavingInference ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <SaveIcon fontSize="small" />
              )}
              {isSavingInference ? 'Saving...' : 'Save to Eval'}
            </button>
          )}

          {isDataFromCache && productRecognitionData && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-md text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="font-medium">Cached Result</span>
            </div>
          )}
        </div>

        {onClearCache && (
          <button
            onClick={onClearCache}
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors"
            title="Clear all cached model results"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Clear Cache
          </button>
        )}
      </div>

      {/* Warning for different image data */}
      {isDataFromDifferentImage && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Data from Different Image</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  The product recognition data shown is from image ID{' '}
                  <strong>{productRecognitionData?.imageId}</strong>, but you're currently viewing
                  image ID <strong>{currentImageId}</strong>. Run the model again to get data for
                  the current image.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {productRecognitionData && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Layers List */}
          <div className="lg:col-span-1 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Layers</h3>
              <button
                onClick={() => {
                  const allLayersEnabled = LAYER_CONFIG.every((layer) =>
                    enabledLayers.has(layer.key)
                  );
                  const newSet = new Set<string>();

                  if (!allLayersEnabled) {
                    // Not all layers are enabled, so enable all
                    LAYER_CONFIG.forEach((layer) => newSet.add(layer.key));
                  }
                  // If all layers are enabled, leave newSet empty to disable all

                  onEnabledLayersChange?.(newSet);
                }}
                className="p-1.5 rounded-md transition-colors bg-gray-100 hover:bg-gray-200 text-gray-600"
                title={
                  LAYER_CONFIG.every((layer) => enabledLayers.has(layer.key))
                    ? 'Disable all layers'
                    : 'Enable all layers'
                }
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {LAYER_CONFIG.every((layer) => enabledLayers.has(layer.key)) ? (
                    // "Uncheck all" icon - square with X
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    // "Check all" icon - checkmark
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  )}
                </svg>
              </button>
            </div>
            <div className="space-y-2">
              {LAYER_CONFIG.map((layer) => {
                const count = getLayerCount(layer.key);
                const isEnabled = enabledLayers.has(layer.key);

                return (
                  <button
                    key={layer.key}
                    onClick={() => toggleLayer(layer.key)}
                    className={`w-full text-left p-3 rounded-md border transition-colors ${
                      isEnabled
                        ? 'border-blue-300 bg-blue-50 text-blue-900'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${layer.color} mr-2`}></div>
                        <span className="text-sm font-medium">{layer.label}</span>
                      </div>
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                        {count}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right Column - Layer Elements */}
          <div className="lg:col-span-3 flex flex-col">
            <h3 className="text-lg font-semibold mb-4">Elements</h3>

            {/* Search Field */}
            <div className="mb-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by OCR text, product names, UPC, or item numbers..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                {searchText && (
                  <button
                    onClick={() => setSearchText('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg
                      className="h-5 w-5 text-gray-400 hover:text-gray-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div
              className="overflow-y-auto"
              style={{ height: 'calc(9 * 3.75rem + 8 * 0.5rem - 4rem)' }}
            >
              {LAYER_CONFIG.map((layer) => renderLayerElements(layer.key))}
              {LAYER_CONFIG.every(
                (layer) => !enabledLayers.has(layer.key) || getLayerCount(layer.key) === 0
              ) && (
                <div className="text-center text-gray-500 py-8">
                  No elements to display. Enable layers or run the model first.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!productRecognitionData && (
        <div className="text-center text-gray-500 py-8">
          Click "Run Model" to generate product recognition data.
        </div>
      )}
    </div>
  );
};
